import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import PlanUpgradeModal from './PlanUpgradeModal';
import { ROUTES } from '@constants';

/**
 * FeatureProtectedRoute - Protects routes based on plan features
 * Prevents direct URL access to restricted features
 */
const FeatureProtectedRoute = ({ children, feature, redirectTo = ROUTES.STUDENT_DASHBOARD }) => {
  const { canAccessFeature, loading } = usePlanRestrictions();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const location = useLocation();

  const hasAccess = canAccessFeature(feature);

  useEffect(() => {
    // If user doesn't have access, show upgrade modal
    if (!loading && !hasAccess) {
      setShowUpgradeModal(true);
    }
  }, [loading, hasAccess]);

  // Show loading state while checking permissions
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  // If no access, show upgrade modal and redirect
  if (!hasAccess) {
    return (
      <>
        <PlanUpgradeModal 
          isOpen={showUpgradeModal} 
          onClose={() => {
            setShowUpgradeModal(false);
            // Redirect after closing modal
            window.location.href = redirectTo;
          }} 
        />
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Feature Locked</h2>
            <p className="text-gray-600 mb-6">
              This feature is not available in your current plan. Please upgrade to access this feature.
            </p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold"
            >
              View Plans
            </button>
          </div>
        </div>
      </>
    );
  }

  // User has access, render the protected content
  return children;
};

export default FeatureProtectedRoute;
